import { globalRef, type Unsubscribe } from '@amazon/vinyl-util'
import { documentEvents } from '@/util/interaction'

/**
 * Seconds of queued work one task may run before yielding, leaving the rest of a 60 Hz frame for
 * input, style, layout and paint.
 */
export const SLICE_BUDGET_SECONDS = 0.008

interface TaskChannel {
    readonly pending: (() => void)[]
    post(): void
}

// A message channel rather than requestIdleCallback or scheduler.yield, which not every target
// engine has, or a timer, which browsers clamp once nested.
const taskChannelRef = globalRef((): TaskChannel => {
    const pending: (() => void)[] = []
    const run = () => pending.shift()?.()
    if (typeof MessageChannel === 'undefined') return { pending, post: () => setTimeout(run) }
    const channel = new MessageChannel()
    channel.port1.onmessage = run
    return { pending, post: () => channel.port2.postMessage(null) }
})

/**
 * Runs `fn` in a task of its own, after the tasks already queued here and after the browser has
 * had the chance to handle input and render. The returned function cancels it if it hasn't run.
 */
export function nextTask(fn: () => void): Unsubscribe {
    const channel = taskChannelRef.value
    let canceled = false
    channel.pending.push(() => {
        if (!canceled) fn()
    })
    channel.post()
    return () => {
        canceled = true
    }
}

/** Resolves in a task of its own, after the tasks already queued here: an `await` that ends a task. */
export function yieldTask(): Promise<void> {
    return new Promise((resolve) => nextTask(resolve))
}

/**
 * Runs `fn` in a task after the next frame renders, when the style and layout that frame computed
 * are still clean to read. The returned function cancels it if it hasn't run.
 */
export function afterNextPaint(fn: () => void): Unsubscribe {
    let cancelTask: Unsubscribe | undefined
    const frame = requestAnimationFrame(() => {
        cancelTask = nextTask(fn)
    })
    return () => {
        cancelAnimationFrame(frame)
        cancelTask?.()
    }
}

interface SliceQueue {
    readonly work: (() => void)[]
    scheduled: boolean
}

const sliceQueueRef = globalRef((): SliceQueue => ({ work: [], scheduled: false }))

/**
 * Runs `work` in a later task, in queue order, packing as much queued work into each task as fits
 * {@link SLICE_BUDGET_SECONDS} (at least one item), so a burst of DOM work spreads over frames
 * instead of stalling one. The returned function cancels it if it hasn't run.
 */
export function runSliced(work: () => void): Unsubscribe {
    const queue = sliceQueueRef.value
    let canceled = false
    queue.work.push(() => {
        if (!canceled) work()
    })
    scheduleSlice(queue)
    return () => {
        canceled = true
    }
}

function scheduleSlice(queue: SliceQueue): void {
    if (queue.scheduled) return
    queue.scheduled = true
    nextTask(() => {
        queue.scheduled = false
        const deadline = performance.now() + SLICE_BUDGET_SECONDS * 1000
        try {
            do queue.work.shift()?.()
            while (queue.work.length > 0 && performance.now() < deadline)
        } finally {
            if (queue.work.length > 0) scheduleSlice(queue)
        }
    })
}

const frameQueueRef = globalRef((): SliceQueue => ({ work: [], scheduled: false }))

/**
 * Runs `work` in a task after a frame renders, one queued item per frame, for DOM work whose cost
 * is the rendering it causes rather than its script: {@link runSliced} would pack several into
 * one task and leave their rendering to one frame. The returned function cancels it if it hasn't
 * run.
 */
export function runPerFrame(work: () => void): Unsubscribe {
    const queue = frameQueueRef.value
    const item = () => work()
    queue.work.push(item)
    scheduleFrame(queue)
    return () => {
        const at = queue.work.indexOf(item)
        if (at !== -1) queue.work.splice(at, 1)
    }
}

function scheduleFrame(queue: SliceQueue): void {
    if (queue.scheduled) return
    queue.scheduled = true
    afterNextPaint(() => {
        queue.scheduled = false
        try {
            queue.work.shift()?.()
        } finally {
            if (queue.work.length > 0) scheduleFrame(queue)
        }
    })
}

interface KeyFlush {
    readonly pending: Set<() => void>
    stopListening: Unsubscribe | null
}

const keyFlushRef = globalRef((): KeyFlush => ({ pending: new Set(), stopListening: null }))

/**
 * {@link runPerFrame}, except that a key press before its frame runs it first, ahead of the
 * press's own handlers: for DOM held back from a page swap that a key press reads, such as the
 * focusables an arrow or Tab moves to. The returned function cancels it if it hasn't run.
 */
export function runPerFrameBeforeKey(work: () => void): Unsubscribe {
    const flush = keyFlushRef.value
    const settle = () => {
        cancelFrame()
        flush.pending.delete(run)
        if (flush.pending.size > 0) return
        flush.stopListening?.()
        flush.stopListening = null
    }
    const run = () => {
        settle()
        work()
    }
    const cancelFrame = runPerFrame(run)
    flush.pending.add(run)
    flush.stopListening ??= documentEvents.on(
        'keydown',
        () => {
            for (const pending of [...flush.pending]) pending()
        },
        { capture: true }
    )
    return settle
}
